import {
  Globe, Users, BookOpen, Trophy, CalendarDays, CreditCard,
  ShieldCheck, Zap, Server, Palette, Layout,
  MessageSquare, Layers, PenTool, GraduationCap, BarChart3, FileCheck,
  Home, Thermometer, PanelTop, Smartphone, Wrench, ShoppingBag, Film, Tag,
  Mountain, TreePine, Sparkles, HardHat, Compass,
  UtensilsCrossed, Wine, Tablet, Settings
} from 'lucide-react';

export const categories = [
  { id: 'all', label: 'Tous les projets' },
  { id: 'site-vitrine', label: 'Site vitrine' },
  { id: 'site-vitrine-avance', label: 'Site vitrine avancé' },
  { id: 'app-web', label: 'App web' },
  { id: 'app-mobile', label: 'App mobile' },
];

const projects = [
  {
    id: 'cycles-ville',
    name: 'Magasin de Cycles',
    category: 'site-vitrine-avance',
    type: 'Site vitrine avancé',
    hidden: true,
    tagline: 'Le vélo par des passionnés, magasin de vélo clé en main',
    url: 'https://cycles.axeris-labs.fr',
    logo: '/images/projects/cycles-1.png',
    logoStyle: 'cover',
    color: '#DC2626',
    forSale: true,
    salePrice: 'Sur devis',
    description: 'Site vitrine avancé pour un magasin de vélo : un seul commerce qui regroupe vente, atelier et location. Atmosphère immersive avec vidéos d\'arrière-plan plein écran, page de vente complète, espace atelier, boutique en ligne, page de contact et page d\'accueil grand format. Le tout pilotable via un menu d\'administration dédié. Ce site clé en main est disponible à l\'acquisition.',
    shortDescription: 'Site vitrine premium pour un magasin de vélo (vente, atelier, location) avec vidéos immersives et back‑office admin. Disponible à l\'achat.',
    badges: [
      { label: 'Site avancé', icon: Globe },
      { label: 'Vidéos immersives', icon: Film },
      { label: 'Multipage', icon: Layout },
      { label: 'Back‑office admin', icon: ShieldCheck },
    ],
    technologies: ['React', 'Tailwind CSS', 'FastAPI', 'MongoDB'],
    featured: false,
    duration: 'Clé en main',
    year: '2026',
    challenges: [
      'Créer un univers immersif qui transmet la passion du vélo dès la page d\'accueil grâce à des vidéos plein écran',
      'Structurer trois métiers distincts (vente, atelier, location) sous un seul site sans perdre le visiteur',
      'Concevoir une page de vente complète permettant de présenter le catalogue par catégorie (Route, VTT, VAE, Ville)',
      'Intégrer un menu d\'administration simple pour permettre au propriétaire de gérer son catalogue et ses pages',
    ],
    features: [
      {
        title: 'Page d\'accueil immersive',
        description: 'Hero plein écran avec vidéo d\'arrière-plan d\'un cycliste en pleine action. Accroche forte "Le vélo par des passionnés" et CTA clairs vers la vente et les services. Une mise en scène cinématographique qui plonge directement le visiteur dans l\'univers du magasin.',
        icon: Home,
        screenshot: '/images/projects/cycles-1.png',
      },
      {
        title: 'Trois métiers, un seul magasin',
        description: 'Section dédiée présentant les trois piliers du magasin : réparation, vente et location. Chaque service est illustré, expliqué et accompagné de filtres rapides (toutes marques, route, VTT, VAE, journée, semaine) pour guider le client vers la bonne offre.',
        icon: Wrench,
        screenshot: '/images/projects/cycles-2.png',
      },
      {
        title: 'Boutique & page de vente',
        description: 'Page boutique complète avec présentation du catalogue de vélos par catégorie. Mise en avant des grandes marques, des nouveautés et des vélos en stock. Idéale pour convertir les visiteurs en clients et générer des demandes d\'essai.',
        icon: ShoppingBag,
        screenshot: '/images/projects/cycles-3.png',
      },
      {
        title: 'Atelier & contact',
        description: 'Page atelier détaillant les prestations de réparation (diagnostic gratuit, réglages, grosses interventions, toutes marques) et page de contact claire pour prendre rendez-vous ou demander un devis. Un parcours simple et rassurant pour le visiteur.',
        icon: MessageSquare,
        screenshot: '/images/projects/cycles-4.png',
      },
      {
        title: 'Back‑office d\'administration',
        description: 'Menu d\'administration permettant au propriétaire de gérer en autonomie son site : ajout/modification de vélos, mise à jour des services et des horaires, gestion des contenus de chaque page. Pas besoin de toucher au code.',
        icon: ShieldCheck,
        screenshot: '/images/projects/cycles-5.png',
      },
    ],
  },
  {
    id: 'helix-education',
    name: 'Helix Education',
    category: 'app-web',
    type: 'App web',
    displayOrder: 1,
    tagline: 'Prépa médecine PASS/LAS 100% en ligne',
    url: 'https://helix-education.fr',
    logo: '/images/projects/helix-logo.png',
    color: '#6366f1',
    description: 'Plateforme complète de préparation aux études de médecine. Gestion multi-école, système de QCM avancé, fiches de révision, examens blancs avec notation automatique, classements, gamification et calendrier de révision basé sur la méthode scientifique des J.',
    shortDescription: 'Plateforme e-learning complète pour la préparation médecine PASS/LAS avec QCM, classements et gamification.',
    badges: [
      { label: 'App web', icon: Globe },
      { label: 'SEO local', icon: ShieldCheck },
      { label: 'Vente en ligne', icon: CreditCard },
      { label: 'Hébergé par Axeris', icon: Server },
    ],
    technologies: ['React', 'FastAPI', 'MongoDB', 'Stripe', 'Python'],
    featured: false,
    duration: '2 mois',
    year: '2025',
    challenges: [
      'Gérer des milliers de QCM avec correction instantanée et statistiques détaillées par matière',
      'Créer un système de classement en temps réel entre étudiants de différentes écoles',
      'Implémenter la méthode des J (répétition espacée) avec un calendrier de révision personnalisé',
      'Intégrer un système de paiement sécurisé avec paiement en 3x sans frais',
    ],
    features: [
      {
        title: 'Gestion multi-école & utilisateurs',
        description: 'Système de rôles avancé : administrateurs, enseignants et étudiants. Chaque école possède son propre espace avec ses propres contenus et paramètres.',
        icon: Users,
        screenshot: '/images/projects/helix-screen-1.png',
      },
      {
        title: 'QCM, fiches & examens blancs',
        description: 'Des milliers de QCM couvrant l\'ensemble du programme PASS/LAS. Fiches de révision interactives et examens blancs avec notation automatique et correction détaillée.',
        icon: BookOpen,
        screenshot: '/images/projects/helix-screen-2.png',
      },
      {
        title: 'Classement & gamification',
        description: 'Système de classement entre étudiants, badges de progression, mini-jeux éducatifs pour rendre l\'apprentissage plus engageant et motivant.',
        icon: Trophy,
        screenshot: '/images/projects/helix-screen-3.png',
      },
      {
        title: 'Calendrier de révision (Méthode des J)',
        description: 'Planification automatique des révisions basée sur la méthode scientifique de la répétition espacée. L\'étudiant sait exactement quoi réviser chaque jour.',
        icon: CalendarDays,
        screenshot: '/images/projects/helix-screen-4.png',
      },
      {
        title: 'Système de vente en ligne',
        description: 'Gestion complète de la vente en ligne, paiement en 3x sans frais, codes promo et garantie "Résultats contre remboursement".',
        icon: CreditCard,
        screenshot: '/images/projects/helix-screen-5.png',
      },
    ],
  },
  {
    id: 'lacolab',
    name: 'LaColab',
    category: 'site-vitrine-avance',
    type: 'Site vitrine avancé',
    displayOrder: 3,
    tagline: 'Accompagnement entrepreneurial BTP – Refonte complète du site web',
    url: 'https://lacolab.biz',
    logo: '/images/logos/lacolab-logo.png',
    color: '#f97316',
    description: 'Refonte intégrale du site web de LaColab, une entreprise d\'accompagnement à la création d\'entreprise dans le bâtiment. Nouveau design immersif au thème sombre avec accents orange, navigation repensée, présentation claire des services d\'accompagnement et formulaire de contact modernisé.',
    shortDescription: 'Refonte complète du site d\'accompagnement entrepreneurial BTP avec design immersif et navigation repensée.',
    badges: [
      { label: 'Site avancé', icon: Globe },
      { label: 'Multipage', icon: Layout },
      { label: 'SEO avancé', icon: ShieldCheck },
      { label: 'Hébergé par Axeris', icon: Server },
    ],
    technologies: ['React', 'Tailwind CSS', 'SEO technique', 'Formulaires avancés'],
    featured: false,
    duration: '2 semaines',
    year: '2026',
    challenges: [
      'Moderniser un site existant tout en conservant l\'identité forte de la marque (orange/noir)',
      'Rendre le parcours utilisateur plus fluide pour convertir les visiteurs en prospects qualifiés',
      'Présenter clairement les 5 piliers d\'accompagnement (méthodologie, réseau, formations...)',
      'Créer une page de contact engageante avec plusieurs canaux de communication',
    ],
    features: [
      {
        title: 'Landing page & identité visuelle',
        description: 'Refonte complète du hero avec un design sombre et immersif. Le message clé "Patron dans le BTP dans 3 mois" est mis en valeur par une typographie percutante et un visuel de chantier captivant.',
        icon: PenTool,
        before: '/images/projects/lacolab-before-1.png',
        after: '/images/projects/lacolab-after-1.png',
      },
      {
        title: 'Présentation des fondateurs',
        description: 'Section "On se présente" entièrement redessinée. Mise en avant de Gwen et Erwan, leurs parcours complémentaires et la philosophie LaColab : l\'humain avant le business, la progression plutôt que la pression.',
        icon: Users,
        before: '/images/projects/lacolab-before-2.png',
        after: '/images/projects/lacolab-after-2.png',
      },
      {
        title: 'Réseau de partenaires',
        description: 'Nouvelle page dédiée au réseau avec 6 catégories de partenaires : fournisseurs matériaux, véhicules, communication, comptables, assureurs et avocats. Tarifs négociés et avantages exclusifs pour chaque domaine.',
        icon: Layers,
        before: '/images/projects/lacolab-before-3.png',
        after: '/images/projects/lacolab-after-3.png',
      },
      {
        title: 'Page de contact modernisée',
        description: 'Formulaire de contact repensé avec champs structurés (nom, email, téléphone, message) et coordonnées directes. Intégration d\'un widget de chat WhatsApp pour les demandes urgentes.',
        icon: MessageSquare,
        before: '/images/projects/lacolab-before-4.png',
        after: '/images/projects/lacolab-after-4.png',
      },
    ],
  },
  {
    id: 'electricite-bechameil',
    name: 'Électricité Béchameil',
    category: 'site-vitrine',
    type: 'Site vitrine',
    displayOrder: 9,
    tagline: 'Électricien dans l\'Ain – Plus de 20 ans d\'expérience',
    url: 'https://electricite-bechameil.fr',
    logo: '/images/projects/bechameil-logo.jpg',
    color: '#ef4444',
    description: 'Site vitrine monopage pour un artisan électricien local. Présentation des services (électricité générale, domotique, bornes de recharge, alarmes), témoignages clients et formulaire de contact. Design professionnel pensé pour la confiance et le SEO local.',
    shortDescription: 'Site vitrine monopage optimisé SEO pour un électricien local avec présentation des services et formulaire de contact.',
    badges: [
      { label: 'Site vitrine', icon: Globe },
      { label: 'Monopage', icon: Zap },
      { label: 'SEO local', icon: ShieldCheck },
      { label: 'Hébergé par Axeris', icon: Server },
    ],
    technologies: ['React', 'Tailwind CSS', 'SEO technique'],
    featured: false,
    duration: '2 jours',
    year: '2026',
    challenges: [
      'Créer un site rapide et efficace qui reflète 20 ans d\'expérience et de sérieux',
      'Intégrer les avis Google pour renforcer la crédibilité auprès des prospects',
      'Présenter clairement les 4 domaines d\'intervention (électricité, domotique, bornes VE, alarmes)',
      'Garantir un temps de chargement ultra-rapide pour ne perdre aucun visiteur mobile',
    ],
    features: [
      {
        title: 'Design professionnel & rassurant',
        description: 'Un design sur-mesure aux couleurs de l\'entreprise, pensé pour inspirer confiance dès la première visite. Mise en avant des certifications, de l\'expérience et des avis Google.',
        icon: Palette,
        screenshot: '/images/projects/bechameil-screen-1.png',
      },
      {
        title: 'Présentation de l\'artisan',
        description: 'Une section dédiée à Sébastien Béchameil, artisan électricien depuis 2002. Mise en avant de ses valeurs : travail soigné, bons conseils et fiabilité. Un vrai gage de confiance.',
        icon: Users,
        screenshot: '/images/projects/bechameil-screen-2.png',
      },
      {
        title: 'Sections services détaillées',
        description: 'Présentation claire des domaines d\'intervention : électricité générale, domotique, bornes de recharge pour véhicules électriques. Chaque service illustré avec des photos de chantier.',
        icon: Layers,
        screenshot: '/images/projects/bechameil-screen-3.png',
      },
      {
        title: 'Témoignages clients',
        description: 'Intégration des avis Google (4,7/5 sur 14 avis) avec des témoignages détaillés. Les clients mettent en avant la qualité du travail, la ponctualité et les bons conseils.',
        icon: MessageSquare,
        screenshot: '/images/projects/bechameil-screen-4.png',
      },
    ],
  },
  {
    id: 'venture-education',
    name: 'Venture Education',
    category: 'app-web',
    type: 'App web',
    displayOrder: 4,
    tagline: 'Prépa certification AMF – +2000 QCM pour réussir votre examen',
    url: 'https://venture-education.fr',
    logo: '/images/projects/venture-logo.png',
    color: '#10B981',
    description: 'Plateforme complète de préparation à la certification AMF (Autorité des Marchés Financiers). Plus de 2000 QCM répartis en 15 chapitres couvrant la finance générale, la déontologie, la réglementation et les instruments financiers. Deux modes de travail : entraînement libre avec corrections détaillées et mode examen chronométré. Panel administrateur pour la gestion des QCM et des utilisateurs.',
    shortDescription: 'Plateforme de préparation à la certification AMF avec +2000 QCM, mode entraînement, mode examen et corrections détaillées.',
    badges: [
      { label: 'App web', icon: Globe },
      { label: 'SEO avancé', icon: ShieldCheck },
      { label: 'Vente en ligne', icon: CreditCard },
      { label: 'Hébergé par Axeris', icon: Server },
    ],
    technologies: ['React', 'FastAPI', 'MongoDB', 'Python'],
    featured: false,
    duration: '1 semaine',
    year: '2026',
    challenges: [
      'Structurer +2000 QCM en 15 chapitres avec deux catégories distinctes (A et C) conformes au programme officiel AMF',
      'Implémenter un mode examen chronométré reproduisant fidèlement les conditions réelles de la certification',
      'Fournir des corrections détaillées et pédagogiques pour chaque question afin de maximiser l\'apprentissage',
      'Créer un panel administrateur complet permettant la gestion des QCM, des chapitres et des utilisateurs',
    ],
    features: [
      {
        title: 'Page d\'accueil & présentation',
        description: 'Landing page moderne et engageante présentant la plateforme. Mise en avant des chiffres clés (+2000 QCM, programme 2026 à jour), des modes d\'entraînement disponibles et d\'un essai gratuit pour convaincre les futurs candidats.',
        icon: Layout,
        screenshot: '/images/projects/venture-screen-1.png',
      },
      {
        title: 'Banque de questions structurée',
        description: 'Tableau de bord "Finance Générale" organisant les 2042 questions en 15 chapitres. Catégorie A (562 questions) et Catégorie C (1480 questions) couvrant la déontologie, la sécurité financière, les abus de marché, les crypto-actifs et la gestion collective.',
        icon: BarChart3,
        screenshot: '/images/projects/venture-screen-2.png',
      },
      {
        title: 'QCM interactif & corrections',
        description: 'Interface de QCM intuitive avec feedback instantané. Chaque question affiche clairement les bonnes et mauvaises réponses avec un code couleur (vert/rouge) et une correction détaillée expliquant la réponse correcte. Suivi de progression en temps réel.',
        icon: FileCheck,
        screenshot: '/images/projects/venture-screen-3.png',
      },
    ],
  },
  {
    id: 'venture-education-app',
    name: 'Venture Education App',
    category: 'app-mobile',
    type: 'App mobile',
    displayOrder: 5,
    tagline: 'Prépa AMF en mobilité – La version app du programme Venture Education',
    logo: '/images/projects/venture-app-logo.png',
    logoStyle: 'app-icon',
    color: '#10B981',
    appStore: 'https://apps.apple.com/gh/app/pr%C3%A9pa-amf-venture-education/id6762098035',
    playStore: 'https://play.google.com/store/apps/details?id=app.archeon.ventureeducation',
    description: 'Version mobile officielle de la plateforme Venture Education. L\'application embarque l\'intégralité du programme de préparation à la certification AMF : +2000 QCM, mode entraînement, mode examen chronométré et corrections détaillées. Conçue pour réviser partout, à tout moment, en toute autonomie.',
    shortDescription: 'L\'application mobile officielle de Venture Education pour réviser l\'AMF partout : +2000 QCM, mode examen et corrections.',
    badges: [
      { label: 'App mobile', icon: Smartphone },
      { label: 'iOS & Android', icon: Layers },
      { label: 'Synchronisée web', icon: Server },
    ],
    technologies: ['React Native', 'FastAPI', 'MongoDB'],
    featured: false,
    duration: '2 semaines',
    year: '2026',
    challenges: [
      'Adapter l\'expérience web (+2000 QCM, mode examen chronométré) à une interface mobile fluide et tactile',
      'Synchroniser la progression de l\'utilisateur entre l\'application mobile et la plateforme web',
      'Publier l\'application sur les deux stores (App Store et Google Play) en respectant leurs guidelines',
    ],
    features: [],
  },
  {
    id: 'bistrot-de-la-neuve',
    name: 'Le Bistrot de la Neuve',
    category: 'site-vitrine-avance',
    type: 'Site vitrine avancé',
    displayOrder: 6,
    tagline: 'Brasserie · Bar · Restaurant – Site vitrine avancé avec carte tablette et back‑office complet',
    url: 'https://bneuve.axeris-labs.fr',
    logo: '/images/projects/bneuve-logo.png',
    color: '#B91C1C',
    description: 'Site vitrine avancé pour Le Bistrot de la Neuve, brasserie‑bar‑restaurant. La partie publique met en scène l\'établissement (hero immersif, plats du jour dynamiques renouvelés chaque semaine, carte des boissons) et facilite la réservation. Une zone privée optimisée tablette affiche les cartes en salle (Carte Bistrot, Carte Bar) directement utilisée par le personnel. Le tout est piloté par un back‑office riche permettant à l\'équipe de gérer plats du jour, formules, cartes, boissons, horaires et pop‑ups sans jamais toucher au code.',
    shortDescription: 'Site vitrine avancé pour une brasserie‑bar‑restaurant : plats du jour dynamiques, carte boissons publique, cartes tablette en salle et back‑office complet.',
    badges: [
      { label: 'Site avancé', icon: Globe },
      { label: 'Menu du jour dynamique', icon: UtensilsCrossed },
      { label: 'Mode tablette en salle', icon: Tablet },
      { label: 'Hébergé par Axeris', icon: Server },
    ],
    technologies: ['React', 'Tailwind CSS', 'FastAPI', 'MongoDB'],
    featured: false,
    duration: '3 semaines',
    year: '2026',
    challenges: [
      'Concevoir une partie publique immersive (hero plein écran, identité bistrot chaleureuse) qui pousse à la réservation et met en valeur les plats du jour de la semaine',
      'Construire des pages tablette dédiées (carte bistrot, carte bar) optimisées pour un usage quotidien par le personnel en salle, sans risque d\'erreur de manipulation',
      'Développer un back‑office riche couvrant plats du jour, formules, menu enfant, boissons, carte bistrot, carte bar, horaires et pop‑ups, avec activation/désactivation et édition rapide',
      'Garantir une synchronisation instantanée entre les modifications faites par l\'équipe en cuisine et l\'affichage côté public et tablettes',
    ],
    features: [
      {
        title: 'Page d\'accueil immersive',
        description: 'Hero plein écran sur l\'intérieur du bistrot, typographie serif rouge et accroche "Brasserie · Bar · Restaurant". CTA principaux vers la découverte des menus et la réservation téléphonique. Une mise en scène chaleureuse qui plonge directement dans l\'ambiance.',
        icon: UtensilsCrossed,
        screenshot: '/images/projects/bneuve-1.png',
      },
      {
        title: 'Plats du jour dynamiques',
        description: 'Section "Les plats du jour" présentant la semaine du Lundi au Vendredi. La carte du jour est mise en surbrillance avec un badge "Aujourd\'hui" calculé automatiquement. Chaque plat est édité depuis l\'admin et synchronisé en temps réel.',
        icon: CalendarDays,
        screenshot: '/images/projects/bneuve-2.png',
      },
      {
        title: 'Carte des boissons publique',
        description: 'Page boissons publique au design éditorial sobre : apéritifs, vins, bières, cocktails. Mise en page typographique soignée avec quantité, descriptif et prix alignés. La carte est gérée 100% depuis le back‑office et publiée en un clic.',
        icon: Wine,
        screenshot: '/images/projects/bneuve-3.png',
      },
      {
        title: 'Mode tablette en salle',
        description: 'Pages secrètes optimisées pour tablette (Restauration & Boissons), utilisées par le personnel en service. Grille pleine page, contrastes forts, navigation tactile rapide. Le serveur dispose d\'une carte toujours à jour sans avoir à imprimer quoi que ce soit.',
        icon: Tablet,
        screenshot: '/images/projects/bneuve-4.png',
      },
      {
        title: 'Back‑office d\'administration',
        description: 'Interface admin avec onglets dédiés (Plats du jour, Formule Saison, Menu Enfant, Boissons, Carte Bistrot, Carte Bar, Horaires, Pop‑ups). Chaque item peut être ajouté, édité, désactivé ou supprimé avec activation visuelle "ACTIF". Tout est pensé pour que l\'équipe pilote son site sans jamais toucher au code.',
        icon: Settings,
        screenshot: '/images/projects/bneuve-5.png',
      },
    ],
  },
  {
    id: 'rozier-concept',
    name: 'Rozier Concept',
    category: 'site-vitrine',
    type: 'Site vitrine',
    displayOrder: 7,
    tagline: 'L\'invisible structure le visible – Bâti, paysage et énergétique en un seul univers',
    url: 'https://rozier-concept.fr',
    logo: '/images/projects/rozier-concept-logo.png',
    color: '#C9A84C',
    description: 'Site vitrine premium de 22 pages pour un artisan polyvalent (bâti, paysage, énergétique). Direction artistique aux tons or, fond minéral et photographies de montagne au format plein écran. Une grosse barre de navigation premium centralise l\'accès aux trois métiers, à la méthode Rozier et à la prise de contact. Chaque univers (Le Bâti, Le Paysage, L\'Énergétique) bénéficie de ses propres sous-pages, soit une arborescence soignée qui valorise la richesse du savoir‑faire.',
    shortDescription: 'Site vitrine premium de 22 pages pour un artisan polyvalent (bâti, paysage, énergétique). Direction artistique or sur fond minéral et navigation premium.',
    badges: [
      { label: 'Site vitrine', icon: Globe },
      { label: '22 pages', icon: Layers },
      { label: 'DA premium or', icon: Sparkles },
      { label: 'Hébergé par Axeris', icon: Server },
    ],
    technologies: ['React', 'Tailwind CSS', 'SEO technique'],
    featured: false,
    duration: '1 semaine',
    year: '2026',
    challenges: [
      'Structurer 22 pages réparties sur trois univers métier (Le Bâti, Le Paysage, L\'Énergétique) sans perdre le visiteur',
      'Créer une direction artistique premium aux tons or sur fond minéral capable de valoriser un artisan polyvalent',
      'Concevoir une barre de navigation massive et lisible permettant un accès instantané aux trois métiers et au contact',
      'Maintenir une cohérence éditoriale forte (typographie serif italique, accroches courtes, photographies grand format) sur l\'intégralité du site',
    ],
    features: [
      {
        title: 'Page d\'accueil cinématographique',
        description: 'Hero plein écran avec photographie de montagne au coucher du soleil. Typographie serif italique pour le titre "L\'invisible structure le visible", accents or sur le mot clé et accroche "Bâti · Paysage · Énergétique". Deux CTA principaux mènent vers la méthode Rozier et les trois métiers.',
        icon: Mountain,
        screenshot: '/images/projects/rozier-concept-1.png',
      },
      {
        title: 'Univers Le Paysage',
        description: 'Page dédiée au paysage avec six expertises listées en grille (Conception 2D/3D, Structuration des espaces, Cours et chemins, Aménagements paysagers, Aménagements extérieurs, Jardins). Visuel vertical immersif d\'un jardin italien et CTA "Découvrir l\'univers" pour aller plus loin.',
        icon: TreePine,
        screenshot: '/images/projects/rozier-concept-2.png',
      },
      {
        title: 'Univers Le Bâti',
        description: 'Hero plein cadre avec photographie de charpente en gros plan. Trois domaines clés présentés ensuite (Charpente et zinguerie, Menuiseries, Isolation extérieure et combles), chacun introduit par un numéro doré et une courte description tournée bénéfice client.',
        icon: HardHat,
        screenshot: '/images/projects/rozier-concept-3.png',
      },
    ],
  },
  {
    id: 'rozier-menuiserie',
    name: 'Rozier Menuiserie',
    category: 'site-vitrine-avance',
    type: 'Site vitrine avancé',
    displayOrder: 8,
    tagline: 'L\'invisible structure le visible – Ossature bois, isolation et menuiseries sur mesure',
    url: '#',
    comingSoon: true,
    logo: '/images/projects/rozier-logo.png',
    color: '#C9A84C',
    description: 'Site vitrine multipage pour une entreprise de menuiserie, ossature bois et isolation thermique. L\'atmosphère dark et premium met en valeur le savoir-faire artisanal avec des visuels immersifs. Chaque page d\'expertise (menuiserie, ossature bois, isolation) présente en détail les services, les applications et les avantages pour le client.',
    shortDescription: 'Site vitrine dark et premium pour un menuisier spécialisé en ossature bois et isolation thermique.',
    badges: [
      { label: 'Site vitrine', icon: Globe },
      { label: 'Multipage', icon: Layers },
      { label: 'Design premium', icon: Palette },
      { label: 'Hébergé par Axeris', icon: Server },
    ],
    technologies: ['HTML', 'CSS', 'JavaScript'],
    featured: false,
    duration: '1 semaine',
    year: '2026',
    challenges: [
      'Créer une atmosphère dark et premium qui reflète le caractère noble du travail du bois',
      'Structurer un site multipage clair avec des pages d\'expertise dédiées (menuiserie, ossature bois, isolation)',
      'Transmettre un côté énergétique et dynamique tout en conservant une identité visuelle élégante',
      'Mettre en valeur les différentes applications (maison, extension, bardage, carport, terrasse) de manière lisible et engageante',
    ],
    features: [
      {
        title: 'Page d\'accueil immersive',
        description: 'Hero section avec fond d\'ossature bois et accroche "L\'invisible structure le visible". Atmosphère dark avec typographie serif élégante et accents dorés. Navigation claire vers les expertises et les réalisations.',
        icon: Home,
        screenshot: '/images/projects/rozier-screen-1.png',
      },
      {
        title: 'Pages expertises détaillées',
        description: 'Section "Nos Expertises" présentant les 3 corps de métier – Isolation thermique, Ossature Bois et Menuiserie – avec des visuels plein cadre et des descriptions concises qui redirigent vers les pages dédiées.',
        icon: PanelTop,
        screenshot: '/images/projects/rozier-screen-2.png',
      },
      {
        title: 'Ossature bois – Structures intégrées',
        description: 'Page dédiée aux structures bois avec une grille de 6 applications (maison, extension, bardage, carport & pergola, terrasses, pool house). Chaque carte détaille les avantages et les points forts de la solution.',
        icon: Layers,
        screenshot: '/images/projects/rozier-screen-3.png',
      },
      {
        title: 'Isolation – Applications ciblées',
        description: 'Page isolation présentant l\'ITE (Isolation Thermique par l\'Extérieur) avec une galerie de types de bâtiments concernés : maison individuelle, bâtiment professionnel, agricole et rénovation lourde.',
        icon: Thermometer,
        screenshot: '/images/projects/rozier-screen-4.png',
      },
    ],
  },
  {
    id: 'helix-j',
    name: 'Helix J',
    category: 'app-mobile',
    type: 'App mobile',
    displayOrder: 2,
    tagline: 'Organisez vos révisions selon la méthode des J',
    logo: '/images/projects/helixj-logo.jpg',
    logoStyle: 'app-icon',
    color: '#A855F7',
    appStore: 'https://apps.apple.com/gh/app/helix-m%C3%A9thode-des-j/id6762047448',
    playStore: 'https://play.google.com/store/apps/details?id=app.archeon.helixj',
    description: 'Application mobile gratuite pour organiser ses révisions selon la méthode des J (J0, J1, J3, J7, J14, J30, J60). Helix J aide les étudiants à planifier automatiquement leurs sessions de révision avec un calendrier intelligent qui optimise la mémorisation à long terme grâce aux rappels espacés.',
    shortDescription: 'Application mobile gratuite pour organiser ses révisions selon la méthode des J.',
    badges: [
      { label: 'App mobile', icon: Smartphone },
      { label: 'iOS & Android', icon: Layers },
      { label: 'Gratuite', icon: Zap },
    ],
    technologies: ['React Native', 'FastAPI', 'MongoDB'],
    featured: false,
    duration: '1 semaine',
    year: '2026',
    challenges: [
      'Implémenter l\'algorithme de la méthode des J avec planification automatique des révisions (J0, J1, J3, J7, J14, J30, J60)',
      'Concevoir une interface mobile intuitive et motivante pour un usage quotidien par les étudiants',
      'Gérer les notifications et rappels de révision pour maximiser l\'assiduité',
    ],
    features: [],
  },
];

export default projects;
